import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { submitInterviewFeedback } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/contexts/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface InterviewFeedbackFormProps {
  interviewId: string;
  interviewTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function InterviewFeedbackForm({
  interviewId,
  interviewTitle,
  open,
  onOpenChange,
  onSuccess,
}: InterviewFeedbackFormProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);

  const mutation = useMutation({
    mutationFn: () => submitInterviewFeedback(interviewId, { feedback, rating }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      addToast({ type: 'success', title: 'Feedback submitted successfully' });
      setFeedback('');
      setRating(0);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      addToast({ type: 'error', title: 'Failed to submit feedback', message: error.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim() || rating === 0) return;
    mutation.mutate();
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setFeedback('');
      setRating(0);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Interview Feedback</DialogTitle>
          <DialogDescription>
            Provide feedback for: {interviewTitle}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating */}
          <div className="space-y-2">
            <Label>Rating</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoveredRating(value)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-0.5 transition-colors"
                >
                  <Star
                    className={`h-6 w-6 ${
                      value <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-text-tertiary'
                    }`}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm text-text-secondary self-center">
                  {rating}/5
                </span>
              )}
            </div>
            {rating === 0 && (
              <p className="text-xs text-text-tertiary">Click a star to rate</p>
            )}
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <Label htmlFor="feedback">Feedback</Label>
            <Textarea
              id="feedback"
              placeholder="How did the interview go? Share your assessment of the candidate..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !feedback.trim() || rating === 0}
            >
              {mutation.isPending ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
